import * as _ from "lodash";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import * as analyticsStore from "store/analytics";
import * as programPricingSlice from "store/programPricing";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";

/**
 * Calculates RPI (Risk Performance Indicator) for a layer
 * Formula: RPI = expiringCombinedRatio / renewalCombinedRatio
 *
 * @param {number} renewalCombinedRatio - Combined ratio from current layer analytics
 * @param {number} expiringCombinedRatio - Combined ratio from prior bound layer analytics
 * @returns {number|null} RPI value or null if calculation cannot be performed
 */
export const calculateRPI = (renewalCombinedRatio, expiringCombinedRatio) => {
  if (renewalCombinedRatio === 0 || renewalCombinedRatio == null) {
    return null;
  }

  if (expiringCombinedRatio === 0 || expiringCombinedRatio == null) {
    return null;
  }

  try {
    const differenceCR = renewalCombinedRatio - expiringCombinedRatio;
    const x = differenceCR / expiringCombinedRatio;
    const rpi = 1 - x;
    return rpi;
  } catch (error) {
    return null;
  }
};

/**
 * Matching tolerance for layer comparison (10%)
 */
const MATCHING_TOLERANCE = 0.1;

/**
 * Checks if two values are within tolerance of each other
 *
 * @param {number} value1 - First value
 * @param {number} value2 - Second value
 * @param {number} tolerance - Tolerance as a decimal (e.g., 0.1 for 10%)
 * @returns {boolean} True if values are within tolerance
 */
const isWithinTolerance = (value1, value2, tolerance = MATCHING_TOLERANCE) => {
  if (value1 == null || value2 == null) return false;
  if (value1 === 0 && value2 === 0) return true;
  if (value1 === 0 || value2 === 0) return false;

  const diff = Math.abs(value1 - value2);
  const avg = (Math.abs(value1) + Math.abs(value2)) / 2;
  return diff / avg <= tolerance;
};

/**
 * Calculates a match score between a current layer and a prior layer
 * Lower score = better match
 * Returns null if layers don't match at all
 *
 * @param {Object} currentLayer - Current layer with limit and attachment
 * @param {Object} priorLayer - Prior layer with limit and attachment
 * @returns {number|null} Match score (0 = exact match) or null if no match
 */
const calculateMatchScore = (currentLayer, priorLayer) => {
  if (!currentLayer || !priorLayer) return null;

  const currentLimit = currentLayer.limit;
  const currentAttachment = currentLayer.attachment;
  const priorLimit = priorLayer.limit;
  const priorAttachment = priorLayer.attachment;

  // Need both limit and attachment to match
  if (currentLimit == null || currentAttachment == null) return null;
  if (priorLimit == null || priorAttachment == null) return null;

  // Exact match
  if (currentLimit === priorLimit && currentAttachment === priorAttachment) {
    return 0;
  }

  // Check if within tolerance
  const limitMatch = isWithinTolerance(currentLimit, priorLimit);
  const attachmentMatch = isWithinTolerance(currentAttachment, priorAttachment);

  if (limitMatch && attachmentMatch) {
    // Calculate score based on how close the match is
    const limitDiff =
      Math.abs(currentLimit - priorLimit) / Math.max(currentLimit, priorLimit);
    const attachmentDiff =
      Math.abs(currentAttachment - priorAttachment) /
      Math.max(currentAttachment, priorAttachment, 1);
    return limitDiff + attachmentDiff;
  }

  return null;
};

/**
 * Finds the best matching prior layer for a current layer based on limit and attachment
 *
 * @param {Object} currentLayer - Current layer to match
 * @param {Array} priorLayers - Array of prior layers to search
 * @returns {Object|null} Best matching prior layer or null if no match found
 */
export const findMatchingPriorLayer = (currentLayer, priorLayers) => {
  if (!currentLayer || !priorLayers || priorLayers.length === 0) {
    return null;
  }

  let bestMatch = null;
  let bestScore = Infinity;

  for (const priorLayer of priorLayers) {
    const score = calculateMatchScore(currentLayer, priorLayer);
    if (score !== null && score < bestScore) {
      bestScore = score;
      bestMatch = priorLayer;
    }
  }

  return bestMatch;
};

/**
 * Finds the bound layer from prior state pricing layers
 *
 * @param {Object} priorState - Prior submission state
 * @returns {Object|null} Bound layer or null if not found
 */
const findPriorBoundLayer = (priorState) => {
  if (!priorState || _.isEmpty(priorState)) {
    return null;
  }

  const priorLayers = priorState?.pricing?.layers || [];
  const boundLayer =
    priorLayers.find((layer) => layer.status === "BOUND") || null;

  return boundLayer;
};

/**
 * Gets the expiring combined ratio from prior analytics for a specific layer
 *
 * @param {Object} priorState - Prior submission state
 * @param {Object} priorLayer - Prior layer to get ratio for
 * @returns {number|null} Expiring combined ratio or null if not found
 */
const getExpiringCombinedRatio = (priorState, priorLayer) => {
  if (!priorLayer || !priorState) {
    return null;
  }

  const priorAnalyticsLayers =
    priorState?.analytics?.programPricing?.values?.layers || {};
  const expiringCombinedRatio =
    priorAnalyticsLayers[priorLayer.id]?.GGLRCombinedRatioFOT || null;

  return expiringCombinedRatio;
};

/**
 * Formats a layer name for display
 *
 * @param {Object} layer - Layer object with limit and attachment
 * @returns {string} Formatted layer name
 */
export const formatLayerName = (layer) => {
  if (!layer) return "Unknown";
  const formatValue = (val) => {
    if (val == null) return "?";
    if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
    if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
    return `$${val}`;
  };
  return `${formatValue(layer.limit)} xs ${formatValue(layer.attachment)}`;
};

/**
 * Match type enum for describing how a prior layer was selected
 */
export const MatchType = {
  RENEWED: "renewed",
  EXACT: "exact",
  CLOSE: "close",
  BOUND_FALLBACK: "bound_fallback",
  MANUAL: "manual",
  NONE: "none",
};

/**
 * Hook for RPI calculation that provides shared validation and calculation logic
 * Now supports per-layer matching and manual prior layer selection
 *
 * @returns {Object} Object containing validation state, calculation function, and prior layer info
 */
export const useRpiCalculation = () => {
  const priorState = useSelector(stateSelectors.selectPriorState);
  const analytics = useSelector(analyticsStore.select);
  const currentLayers = useSelector(pricingSelectors.selectResolvedLayers);
  const programPricing = useSelector(programPricingSlice.select);

  // Get all prior layers (resolved)
  const priorLayers = useMemo(() => {
    if (!priorState || _.isEmpty(priorState)) {
      return [];
    }
    return pricingSelectors.selectResolvedLayers(priorState) || [];
  }, [priorState]);

  const validation = useMemo(() => {
    // Check 1: Prior state exists
    if (!priorState || _.isEmpty(priorState)) {
      return {
        shouldShow: false,
        error: null,
        boundLayer: null,
        priorLayers: [],
      };
    }

    // Check 2: Prior layers exist
    if (
      !priorState.pricing?.layers ||
      !Array.isArray(priorState.pricing.layers) ||
      priorState.pricing.layers.length === 0
    ) {
      return {
        shouldShow: false,
        error: null,
        boundLayer: null,
        priorLayers: [],
      };
    }

    const boundLayer = findPriorBoundLayer(priorState);

    // Check 3: Prior analytics data exists
    const priorAnalyticsLayers =
      priorState?.analytics?.programPricing?.values?.layers || {};
    const hasAnyPriorAnalytics = Object.keys(priorAnalyticsLayers).length > 0;

    if (!hasAnyPriorAnalytics) {
      return {
        shouldShow: false,
        error: {
          message: "No prior analytics data available for RPI calculation",
          variant: "warning",
        },
        boundLayer,
        priorLayers,
      };
    }

    // Check 4: Current analytics data exists
    const currentAnalyticsLayers =
      analytics?.programPricing?.values?.layers || {};

    if (!analytics?.programPricing?.values?.layers) {
      return {
        shouldShow: false,
        error: {
          message: "Missing current layer analytics data for RPI calculation",
          variant: "warning",
        },
        boundLayer,
        priorLayers,
      };
    }

    // Check 5: Valid combined ratio data exists on current layers
    const hasValidCurrentLayerData = Object.values(currentAnalyticsLayers).some(
      (layerData) => {
        const hasFotRatio =
          layerData?.GGLRCombinedRatioFOT != null &&
          layerData?.GGLRCombinedRatioFOT !== 0;
        const hasQuoteRatio =
          layerData?.GGLRCombinedRatioQuote != null &&
          layerData?.GGLRCombinedRatioQuote !== 0;
        return hasFotRatio || hasQuoteRatio;
      }
    );

    if (!hasValidCurrentLayerData) {
      return {
        shouldShow: false,
        error: {
          message:
            "No current layers have combined ratio data available for RPI calculation",
          variant: "warning",
        },
        boundLayer,
        priorLayers,
      };
    }

    return {
      shouldShow: true,
      error: null,
      boundLayer,
      priorLayers,
    };
  }, [priorState, analytics, priorLayers]);

  /**
   * Gets the prior layer to use for RPI calculation for a given current layer
   * Priority: 1) Manual selection, 2) Auto-matched layer, 3) Bound layer fallback
   */
  const getPriorLayerForCurrentLayer = useCallback(
    (currentLayerId) => {
      if (!validation.shouldShow) {
        return { priorLayer: null, matchType: MatchType.NONE };
      }

      // Check for manual selection first
      const manualSelection =
        programPricing?.layers?.[currentLayerId]?.selectedPriorLayerIdForRpi;

      if (manualSelection) {
        const manualLayer = priorLayers.find((l) => l.id === manualSelection);
        if (manualLayer) {
          return { priorLayer: manualLayer, matchType: MatchType.MANUAL };
        }
      }

      // Try auto-matching based on limit and attachment
      const currentLayer = currentLayers.find((l) => l.id === currentLayerId);
      if (currentLayer) {
        const matchedLayer = findMatchingPriorLayer(currentLayer, priorLayers);
        if (matchedLayer) {
          // Determine if it's an exact or close match
          const isExact =
            currentLayer.limit === matchedLayer.limit &&
            currentLayer.attachment === matchedLayer.attachment;
          return {
            priorLayer: matchedLayer,
            matchType: isExact ? MatchType.EXACT : MatchType.CLOSE,
          };
        }
      }

      // Fall back to bound layer
      if (validation.boundLayer) {
        return {
          priorLayer: validation.boundLayer,
          matchType: MatchType.BOUND_FALLBACK,
        };
      }

      return { priorLayer: null, matchType: MatchType.NONE };
    },
    [validation, priorLayers, currentLayers, programPricing]
  );

  const calculateRpiForLayer = useCallback(
    (layerId) => {
      if (!validation.shouldShow) {
        return null;
      }

      // Prefer backend RPI if available
      const currentAnalyticsLayers =
        analytics?.programPricing?.values?.layers || {};
      const layerData = currentAnalyticsLayers[layerId];

      if (layerData?.rpi != null && layerData.rpi !== undefined) {
        // Backend RPI is available
        return layerData.rpi;
      }

      // Fallback to frontend calculation
      const { priorLayer } = getPriorLayerForCurrentLayer(layerId);
      if (!priorLayer) {
        return null;
      }

      const expiringCombinedRatio = getExpiringCombinedRatio(
        priorState,
        priorLayer
      );
      if (expiringCombinedRatio == null) {
        return null;
      }

      if (!layerData) {
        return null;
      }

      // Try FOT ratio first, then fall back to Quote ratio for renewal
      const renewalCombinedRatio =
        layerData.GGLRCombinedRatioFOT ||
        layerData.GGLRCombinedRatioQuote ||
        null;

      if (renewalCombinedRatio == null) {
        return null;
      }

      return calculateRPI(renewalCombinedRatio, expiringCombinedRatio);
    },
    [validation, analytics, priorState, getPriorLayerForCurrentLayer]
  );

  /**
   * Gets RPI and match info for a layer, preferring backend values but falling back to frontend calculation
   * Returns { rpi, priorLayer, matchType }
   */
  const getRpiForLayer = useCallback(
    (layerId) => {
      if (!validation.shouldShow) {
        return { rpi: null, priorLayer: null, matchType: MatchType.NONE };
      }

      const currentAnalyticsLayers =
        analytics?.programPricing?.values?.layers || {};
      const layerData = currentAnalyticsLayers[layerId];

      // Prefer backend values if available
      if (layerData?.rpi != null && layerData.rpi !== undefined) {
        const backendMatchType = layerData.rpiMatchType || MatchType.NONE;
        const backendPriorLayerId = layerData.rpiPriorLayerId;

        // Find the prior layer if we have an ID
        let priorLayer = null;
        if (backendPriorLayerId) {
          priorLayer = priorLayers.find((l) => l.id === backendPriorLayerId);
        }

        // If we couldn't find the layer, fall back to frontend calculation
        if (!priorLayer && backendMatchType !== MatchType.NONE) {
          // Fall through to frontend calculation
        } else {
          return {
            rpi: layerData.rpi,
            priorLayer,
            matchType: backendMatchType,
          };
        }
      }

      // Fallback to frontend calculation
      const { priorLayer, matchType } = getPriorLayerForCurrentLayer(layerId);
      const rpi = calculateRpiForLayer(layerId);

      return {
        rpi,
        priorLayer,
        matchType,
      };
    },
    [
      validation,
      analytics,
      priorLayers,
      getPriorLayerForCurrentLayer,
      calculateRpiForLayer,
    ]
  );

  return {
    validation,
    calculateRpiForLayer,
    getPriorLayerForCurrentLayer,
    getRpiForLayer,
    priorLayers,
  };
};
